<template>
  <div>
    <vx-card class="mb-4">
      <!-- Cabeçalho: aula + navegação da janela -->
      <div class="flex flex-wrap items-center justify-between mb-4">
        <div>
          <h4 class="mb-1">{{ $t('lesson.agenda.title') }}</h4>
          <div class="text-muted" v-if="lessonName">{{ lessonName }}</div>
        </div>
        <div class="flex items-center gap-2">
          <b-button variant="outline-secondary" size="sm" class="mr-1" @click="goBack">
            <feather-icon icon="ArrowLeftIcon" class="mr-50"/>
            <span class="align-middle">{{ $t('back') }}</span>
          </b-button>
          <b-button variant="outline-primary" size="sm" class="mr-1" @click="previousWindow">
            <feather-icon icon="ChevronLeftIcon"/>
          </b-button>
          <span class="font-medium mx-1">{{ windowLabel }}</span>
          <b-button variant="outline-primary" size="sm" class="mr-1" @click="nextWindow">
            <feather-icon icon="ChevronRightIcon"/>
          </b-button>
          <b-button variant="outline-primary" size="sm" @click="goToday">
            {{ $t('lesson.agenda.today') }}
          </b-button>
        </div>
      </div>

      <!-- Dias -->
      <div v-if="visibleDays.length === 0" class="text-center text-muted p-4">
        {{ $t('lesson.agenda.empty_window') }}
      </div>

      <div v-for="day in visibleDays" :key="day.date" class="mb-3">
        <div class="font-bold border-b pb-1 mb-2 capitalize">
          {{ formatDayHeader(day) }}
        </div>

        <div
          v-for="(slot, slotIndex) in day.slots"
          :key="`${day.date}-${slot.startTime}-${slotIndex}`"
          class="border rounded p-2 mb-2"
          :class="{ 'opacity-75': slot.status === 'CA' }">
          <div class="flex flex-wrap items-center justify-between gap-2">
            <!-- Horário + ocupação -->
            <div class="flex items-center gap-2">
              <feather-icon icon="ClockIcon" class="h-4 w-4 mr-1"/>
              <span class="font-medium mr-2">{{ slot.startTime }} - {{ slot.endTime }}</span>
              <span
                class="cursor-pointer mr-2"
                @click="toggleStudents(day.date, slotIndex)"
                v-tooltip.auto="{
                  content: $t('lesson.agenda.show_students'),
                  delay: { show: 500, hide: 100 },
                }">
                <feather-icon icon="UsersIcon" class="h-4 w-4 mr-50"/>
                <span class="font-bold">{{ slot.occupied }}/{{ slot.capacity === null ? '∞' : slot.capacity }}</span>
                <!-- Turma cheia de assinantes e turma cheia de avulsos têm o
                     mesmo número e significados de negócio opostos. -->
                <small class="text-muted ml-50" v-if="compositionLabel(slot)">{{ compositionLabel(slot) }}</small>
              </span>

              <!-- Badges -->
              <vs-chip :color="capacitySourceColor(slot)" class="mr-1">
                {{ capacitySourceLabel(slot) }}
              </vs-chip>
              <vs-chip :color="statusColor(slot)">
                {{ statusLabel(slot) }}
              </vs-chip>
              <vs-chip color="dark" v-if="slot.off_grid" class="ml-1">
                {{ $t('lesson.agenda.off_grid') }}
              </vs-chip>
            </div>

            <!-- Ações -->
            <div class="flex items-center gap-2" v-if="!isPast(day.date) && slot.status !== 'CA'">
              <feather-icon
                v-permission="'lessons.edit'"
                icon="EditIcon"
                class="h-5 w-5 mr-1 stroke-current text-warning cursor-pointer"
                v-tooltip.auto="{ content: $t('lesson.agenda.adjust_capacity'), delay: { show: 500, hide: 100 } }"
                @click="openAction('capacity', day, slot)"/>
              <feather-icon
                v-permission="'lessons.edit'"
                :icon="slot.blocked ? 'UnlockIcon' : 'LockIcon'"
                class="h-5 w-5 mr-1 stroke-current cursor-pointer"
                :class="slot.blocked ? 'text-success' : 'text-dark'"
                v-tooltip.auto="{
                  content: slot.blocked ? $t('lesson.agenda.unblock') : $t('lesson.agenda.block'),
                  delay: { show: 500, hide: 100 },
                }"
                @click="slot.blocked ? removeOverride(slot) : openAction('block', day, slot)"/>
              <feather-icon
                v-permission="'lessons.edit'"
                icon="RotateCcwIcon"
                v-if="slot.override_id && !slot.blocked"
                class="h-5 w-5 mr-1 stroke-current text-primary cursor-pointer"
                v-tooltip.auto="{ content: $t('lesson.agenda.reset_override'), delay: { show: 500, hide: 100 } }"
                @click="removeOverride(slot)"/>
              <feather-icon
                v-permission="'lessons.seat_grant'"
                icon="UserPlusIcon"
                class="h-5 w-5 mr-1 stroke-current text-success cursor-pointer"
                v-tooltip.auto="{ content: $t('schedule_event.seat_grant'), delay: { show: 500, hide: 100 } }"
                @click="openAction('grant', day, slot)"/>
              <feather-icon
                v-permission="'cancellation_requests.edit'"
                icon="XCircleIcon"
                class="h-5 w-5 stroke-current text-danger cursor-pointer"
                v-tooltip.auto="{ content: $t('lesson.agenda.cancel_occurrence'), delay: { show: 500, hide: 100 } }"
                @click="openAction('cancel', day, slot)"/>
            </div>
          </div>

          <!-- Motivo da exceção -->
          <div v-if="slot.reason" class="mt-1">
            <small class="text-muted">{{ $t('lesson.agenda.reason') }}: {{ slot.reason }}</small>
          </div>

          <!-- Alunos -->
          <div v-if="isExpanded(day.date, slotIndex)" class="mt-2 border-t pt-2">
            <div v-if="slot.students.length === 0">
              <small class="text-muted">{{ $t('lesson.agenda.no_students') }}</small>
            </div>
            <div
              v-for="student in slot.students"
              :key="student.scheduled_event_id"
              class="flex items-center justify-between py-50">
              <small>{{ student.name }} <span class="text-muted">({{ student.email }})</span></small>
              <vs-chip :color="originColor(student.origin)" class="ml-2">
                {{ student.origin_label || '—' }}
              </vs-chip>
            </div>

            <!-- Autorizações nominais vigentes: quem pode entrar acima do
                 limite, mesmo antes de ter comprado. -->
            <div v-if="slot.seat_grants && slot.seat_grants.length" class="mt-2 border-t pt-2">
              <small class="text-muted d-block mb-1">{{ $t('lesson.agenda.seat_grants_title') }}</small>
              <div
                v-for="grant in slot.seat_grants"
                :key="`grant-${grant.id}`"
                class="flex items-center justify-between py-50">
                <small>
                  {{ grant.user_name }}
                  <span class="text-muted">— {{ grant.scope_label }}</span>
                  <span class="text-muted" v-if="grant.reason">· {{ grant.reason }}</span>
                </small>
                <feather-icon
                  v-permission="'lessons.seat_grant'"
                  icon="XIcon"
                  style="cursor: pointer;"
                  class="h-4 w-4 stroke-current text-danger"
                  v-tooltip.auto="{ content: $t('lesson.agenda.revoke_seat_grant'), delay: { show: 500, hide: 100 } }"
                  @click="confirmRevokeGrant(grant)"/>
              </div>
            </div>
          </div>
        </div>
      </div>
    </vx-card>

    <occurrence-action-modal
      ref="actionModal"
      :lesson-id="lessonId"
      :lesson-name="lessonName"
      @saved="loadData"/>
  </div>
</template>

<script>
import { BButton } from 'bootstrap-vue'
import LessonService from '@/services/api/LessonService'
import OccurrenceActionModal from './OccurrenceActionModal.vue'

export default {
  components: {
    BButton,
    OccurrenceActionModal
  },
  props: {
    id: {
      type: [Number, String],
      default: null
    }
  },
  data: () => ({
    service: null,
    lessonName: '',
    startDate: null,
    windowDays: 14,
    days: [],
    expanded: {}
  }),
  computed: {
    lessonId() {
      return Number(this.id)
    },
    visibleDays() {
      return this.days.filter(day => day.slots && day.slots.length > 0)
    },
    windowLabel() {
      if (!this.startDate) return ''
      const start = this.parseDate(this.startDate)
      const end = new Date(start)
      end.setDate(end.getDate() + this.windowDays - 1)
      return `${this.formatShort(start)} - ${this.formatShort(end)}`
    }
  },
  methods: {
    /**
     * "5 fixos · 3 avulsos" — só aparece quando há mistura ou assinantes; num
     * clube que só vende avulso a linha seria ruído.
     */
    compositionLabel(slot) {
      const fixos = Number(slot.occupied_subscription || 0)
      const avulsos = Number(slot.occupied_single || 0)
      if (fixos === 0) return ''

      const partes = [`${fixos} fixo${fixos === 1 ? '' : 's'}`]
      if (avulsos > 0) partes.push(`${avulsos} avulso${avulsos === 1 ? '' : 's'}`)

      return `· ${partes.join(' · ')}`
    },
    originColor(origin) {
      return { AS: 'success', RP: 'warning', MN: 'dark', AV: 'primary' }[origin] || 'primary'
    },
    confirmRevokeGrant(grant) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'success',
        title: 'Confirmação',
        acceptText: 'Sim',
        cancelText: 'Não',
        text: `Revogar a autorização de ${grant.user_name}?`,
        accept: () => {
          this.$vs.loading()
          this.service.revokeSeatGrant(this.lessonId, grant.id).then(
            () => {
              this.notifySuccess(this.$vs, this.$t('messages.success'))
              this.loadData()
            },
            error => this.notifyErrors(this.$vs, error),
          ).finally(() => { this.$vs.loading.close() })
        },
      })
    },
    loadData() {
      this.$vs.loading()
      this.service.getOccurrences(this.lessonId, this.startDate, this.windowDays).then(
        response => {
          this.days = response.days || []
          this.expanded = {}
        },
        () => {}
      ).finally(() => { this.$vs.loading.close() })
    },
    loadLesson() {
      this.service.read(this.lessonId).then(
        lesson => { this.lessonName = lesson ? lesson.name : '' },
        () => {}
      )
    },
    previousWindow() {
      this.shiftWindow(-this.windowDays)
    },
    nextWindow() {
      this.shiftWindow(this.windowDays)
    },
    goToday() {
      this.startDate = this.toDateString(new Date())
      this.loadData()
    },
    shiftWindow(deltaDays) {
      const date = this.parseDate(this.startDate)
      date.setDate(date.getDate() + deltaDays)
      this.startDate = this.toDateString(date)
      this.loadData()
    },
    goBack() {
      this.$router.push(`/lessons/${this.lessonId}/edit`)
    },
    openAction(mode, day, slot) {
      this.$refs.actionModal.open(mode, {
        date: day.date,
        weekday: day.weekday,
        startTime: slot.startTime,
        endTime: slot.endTime,
        capacity: slot.capacity,
        capacitySource: slot.capacity_source,
        occupied: slot.occupied,
        overrideId: slot.override_id,
        blocked: slot.blocked,
        reason: slot.reason
      })
    },
    removeOverride(slot) {
      if (!slot.override_id) return
      this.$vs.dialog({
        type: 'confirm',
        color: 'success',
        title: this.$t('lesson.agenda.reset_override'),
        acceptText: this.$t('yes'),
        cancelText: this.$t('no'),
        text: this.$t('lesson.agenda.reset_override_confirm'),
        accept: () => {
          this.$vs.loading()
          this.service.deleteOccurrenceOverride(this.lessonId, slot.override_id).then(
            () => {
              this.notifySuccess(this.$vs, this.$t('messages.success'))
              this.loadData()
            },
            () => {}
          ).finally(() => { this.$vs.loading.close() })
        }
      })
    },
    toggleStudents(date, slotIndex) {
      const key = `${date}-${slotIndex}`
      this.$set(this.expanded, key, !this.expanded[key])
    },
    isExpanded(date, slotIndex) {
      return !!this.expanded[`${date}-${slotIndex}`]
    },
    isPast(date) {
      return date < this.toDateString(new Date())
    },
    capacitySourceLabel(slot) {
      const labels = {
        override: this.$t('lesson.agenda.source_override'),
        schedule: this.$t('lesson.agenda.source_schedule'),
        lesson: this.$t('lesson.agenda.source_lesson')
      }
      return labels[slot.capacity_source] || labels.lesson
    },
    capacitySourceColor(slot) {
      return { override: 'warning', schedule: 'primary', lesson: 'dark' }[slot.capacity_source] || 'dark'
    },
    statusLabel(slot) {
      if (slot.status === 'CA') return this.$t('lesson.agenda.status_cancelled')
      if (slot.blocked) return this.$t('lesson.agenda.status_blocked')
      if (slot.capacity !== null && slot.occupied >= slot.capacity) return this.$t('lesson.agenda.status_full')
      return this.$t('lesson.agenda.status_available')
    },
    statusColor(slot) {
      if (slot.status === 'CA') return 'danger'
      if (slot.blocked) return 'dark'
      if (slot.capacity !== null && slot.occupied >= slot.capacity) return 'warning'
      return 'success'
    },
    formatDayHeader(day) {
      const date = this.parseDate(day.date)
      return date.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })
    },
    formatShort(date) {
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
    },
    parseDate(value) {
      const [year, month, day] = value.split('-').map(Number)
      return new Date(year, month - 1, day)
    },
    toDateString(date) {
      const month = `${date.getMonth() + 1}`.padStart(2, '0')
      const day = `${date.getDate()}`.padStart(2, '0')
      return `${date.getFullYear()}-${month}-${day}`
    }
  },
  beforeMount() {
    this.service = LessonService.build(this.$vs)
    this.startDate = this.toDateString(new Date())
  },
  mounted() {
    if (this.lessonId) {
      this.loadLesson()
      this.loadData()
    }
  }
}
</script>
